import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { BRAND } from "../lib/brand";
import { container, item } from "../lib/motion";
import { MessageSkeleton } from "../components/Skeleton";
import { Heart, Send, Sparkles, Loader2, RefreshCw } from "lucide-react";

export default function Community() {
  const [messages, setMessages] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [text, setText] = useState("");
  const [alias, setAlias] = useState("");
  const [liked, setLiked] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true); setError(false);
    try {
      const [m, f] = await Promise.all([api.get("/community"), api.get("/community/featured")]);
      setMessages(m.data); setFeatured(f.data?.likes > 0 ? f.data : null);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const post = async (e) => {
    e.preventDefault();
    if (!text.trim() || posting) return;
    setPosting(true);
    try {
      const { data } = await api.post("/community", { message: text, alias: alias.trim() || "Anonymous" });
      // Immediately show the new message on the wall (uses the saved doc returned by the API)
      setMessages((prev) => [data, ...prev]);
      setText(""); setAlias("");
      toast.success("Your light has been shared");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post your message. Please try again.");
    } finally {
      setPosting(false);
    }
  };
  const like = async (id) => {
    if (liked.has(id)) return;
    try {
      const { data } = await api.post(`/community/${id}/like`);
      setLiked(new Set([...liked, id]));
      setMessages((prev) => prev.map((m) => (m.id === id ? data : m)));
    } catch {
      toast.error("Could not send your heart. Please try again.");
    }
  };

  return (
    <Layout glow="split" backdrop={BRAND.atmosphere}>
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
        <motion.div initial="hidden" animate="show" variants={container} className="text-center mb-14">
          <motion.span variants={item} className="eyebrow">Encouragement Wall</motion.span>
          <motion.h1 variants={item} className="font-head text-4xl lg:text-6xl font-light mt-4 tracking-tight">Leave a light for a stranger</motion.h1>
          <motion.p variants={item} className="text-zinc-500 mt-5 max-w-xl mx-auto font-light text-lg">Anonymous, uplifting words from people walking the same path. Someone needs yours today.</motion.p>
        </motion.div>

        {featured && (
          <motion.div initial={{ opacity: 0, scale: 0.97, filter: "blur(8px)" }} animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }} transition={{ duration: 0.7 }}
            className="relative glass-card p-12 text-center mb-14 overflow-hidden" data-testid="community-featured">
            <div className="absolute -top-12 -right-12 w-52 h-52 bg-[#4da6ff]/15 blur-[80px] rounded-full" />
            <Sparkles className="w-6 h-6 text-[#4da6ff] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#4da6ff] mb-4">Featured Today</p>
            <p className="font-head text-2xl md:text-3xl font-light max-w-2xl mx-auto tracking-tight leading-snug">"{featured.message}"</p>
            <p className="text-zinc-500 text-sm mt-5 font-light">— {featured.alias} · {featured.likes} hearts</p>
          </motion.div>
        )}

        <motion.form initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} onSubmit={post} className="glass-card p-7 mb-12" data-testid="community-form">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={400} placeholder="Write something uplifting..." data-testid="community-message-input"
            className="w-full field px-4 py-3 resize-none" />
          <div className="flex gap-3 mt-3 flex-wrap">
            <input value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="Your alias (optional)" data-testid="community-alias-input"
              className="flex-1 min-w-[140px] field px-4 py-2.5 text-sm" />
            <button type="submit" disabled={posting} data-testid="community-post-button" className="btn-primary font-medium px-6 py-2.5 flex items-center gap-2 disabled:opacity-60">
              {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {posting ? "Sharing..." : "Share"}
            </button>
          </div>
        </motion.form>

        {error && (
          <div className="glass-card p-10 text-center mb-10" data-testid="community-error">
            <p className="text-zinc-300 font-light">We couldn't reach the wall right now.</p>
            <button onClick={load} data-testid="community-retry-button" className="btn-glass mt-5 px-6 py-2.5 text-sm inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Try again</button>
          </div>
        )}
        {!loading && !error && messages.length === 0 && (
          <p className="text-center text-zinc-500 font-light mb-10" data-testid="community-empty">The wall is quiet for now. Be the first to leave a light.</p>
        )}

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={container}
          className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]" data-testid="community-wall">
          {loading && [0, 1, 2, 3, 4, 5].map((s) => <div key={s} className="mb-6 break-inside-avoid"><MessageSkeleton /></div>)}
          {!loading && messages.map((m) => (
            <motion.div key={m.id} variants={item} className="glass-card p-7 mb-6 break-inside-avoid flex flex-col" data-testid={`community-message-${m.id}`}>
              <p className="text-zinc-200 leading-relaxed flex-1 font-light">"{m.message}"</p>
              <div className="flex items-center justify-between mt-6">
                <span className="text-sm text-zinc-500 font-light">— {m.alias}</span>
                <button onClick={() => like(m.id)} data-testid="community-post-like-button"
                  className={`flex items-center gap-1.5 text-sm transition-colors ${liked.has(m.id) ? "text-[#fb7185]" : "text-zinc-500 hover:text-[#fb7185]"}`}>
                  <Heart className="w-4 h-4" fill={liked.has(m.id) ? "currentColor" : "none"} /> {m.likes}
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </Layout>
  );
}
